import React, { useState, useRef } from 'react';
import { useAcceptInvite } from '../hooks/mutations/useAcceptInvite';
import { ChevronRightIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Member {
  id: string;
  user_name: string;
  user_id: string | null;
}

interface GroupJoinModalProps {
  groupId: string;
  groupName: string;
  linkedMembers: Member[];
  unlinkedMembers: Member[];
  currentUserId?: string;
  currentUserName?: string;
  closeModal: () => void;
  hasPassword: boolean;
}

const GroupJoinModal: React.FC<GroupJoinModalProps> = ({
  groupId,
  groupName,
  linkedMembers,
  unlinkedMembers,
  currentUserId,
  closeModal,
  hasPassword
}) => {
  const [password, setPassword] = useState('');
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const isLoadingRef = useRef(false);
  const acceptInviteMutation = useAcceptInvite();
  const navigate = useNavigate();
  // 기존 멤버와 연결하는 함수
  const handleLinkToExistingMember = async (memberId: string, password?: string) => {
    if (!currentUserId || isLoadingRef.current) return;
    
    try {
      isLoadingRef.current = true;
      await acceptInviteMutation.mutateAsync({ groupId, memberId, password });
      closeModal();
    } catch (error) {
      // 에러는 mutation의 onError에서 처리됨
      console.error('멤버 연결 실패:', error);
    } finally {
      isLoadingRef.current = false;
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <h2 className="text-[22px] font-bold mb-4">{groupName}에 입장하세요!</h2>
      <p className="text-gray-800 mb-2 text-lg">
        {
          isPasswordModalOpen ? (
            "비밀번호를 입력해주세요"
          ) : (
            unlinkedMembers.length > 0 ? '당신의 이름을 선택해 입장하세요' : '그룹 슬롯이 가득찼습니다.'
          )
        }
      </p>
      <div className="space-y-2">
        {
          isPasswordModalOpen ? (
            <>
              <input type="text" 
                value={password}
                inputMode='numeric'
                onChange={(e) => {
                  e.preventDefault();
                  setPassword(e.target.value.replace(/[^0-9]/g, ''))
                }}
                onFocus={(e) => e.target.select()}
                placeholder='4자리 숫자를 입력해주세요'
                minLength={4}
                maxLength={4}
                className="w-full"
              />
              <div className="w-full flex gap-2 mt-2">
                <button 
                  className="w-full text-center px-4 py-3.5 font-semibold bg-gray-50 border border-gray-100 text-gray-800 text-lg rounded-xl"
                  onClick={() => setIsPasswordModalOpen(false)}
                  disabled={acceptInviteMutation.isPending}
                >
                  뒤로가기
                </button>
                <button 
                  className="w-full text-center px-4 py-3.5 font-semibold text-white bg-blue-500 text-lg rounded-xl"
                  onClick={() => {
                    if (password.length === 4 && password.match(/^\d{4}$/)) {
                      handleLinkToExistingMember(selectedMember?.id, password)
                    }
                  }}
                  disabled={password.length !== 4 || !password.match(/^\d{4}$/) || acceptInviteMutation.isPending}
                >
                  입장하기
                </button>
              </div>
            </>
          ): (
            <div className='flex flex-col gap-2 max-h-72 overflow-y-auto'>
              {linkedMembers.map(member => (
                <button
                  key={member.id}
                  className="w-full px-4 py-3.5 bg-gray-50 border border-gray-100 text-lg rounded-xl text-left flex items-center"
                  disabled={true}
                >
                  <span className="flex-grow">{member.user_name}</span>
                  <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                </button>
              ))}
              {unlinkedMembers.map(member => (
                <button
                  key={member.id}
                  className="w-full px-4 py-3.5 bg-gray-50 border border-gray-100 text-lg rounded-xl text-left flex items-center"
                  onClick={() => {
                    if (hasPassword) {
                      setSelectedMember(member);
                      setIsPasswordModalOpen(true);
                    } else {
                      handleLinkToExistingMember(member.id);
                    }
                  }}
                  disabled={acceptInviteMutation.isPending}
                >
                  <span className="flex-grow">{member.user_name}</span>
                  <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                </button>
              ))}
            </div>
          )
        }
        {
          unlinkedMembers.length === 0 && (
            <div className="w-full flex gap-2 mt-4"> 
              <button 
                className="w-full text-center px-4 py-3.5 font-semibold bg-gray-50 border border-gray-100 text-gray-800 text-lg rounded-xl"
                onClick={() => {
                  navigate('/groups', { replace: true });
                  closeModal();
                }}
              >
                목록으로
              </button>
            </div>
          )
        }
      </div>
    </div>
  );
};

export default GroupJoinModal; 